import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Loader2, Send, Phone, Mail, MapPin } from "lucide-react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";

const PRODUCT_OPTIONS = [
  { value: "haldi-turmeric", label: "Haldi (Turmeric Powder)" },
  { value: "spices", label: "Whole & Ground Spices" },
  { value: "vegetables", label: "Fresh & Dehydrated Vegetables" },
  { value: "onion-powder", label: "Onion Powder" },
  { value: "coconut-shell-powder", label: "Coconut Shell Powder" },
  { value: "chocolates", label: "Chocolates" },
  { value: "match-boxes", label: "Match Boxes (Safety Matches)" },
  { value: "wheat-aata", label: "Wheat Aata (Whole Wheat Flour)" },
  { value: "other", label: "Other / Multiple" },
];

const initialState = {
  name: "",
  email: "",
  phone: "",
  country: "",
  company: "",
  product_interest: "",
  quantity: "",
  message: "",
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const InquiryForm = ({ presetProduct = "" }) => {
  const [form, setForm] = useState(() => ({
    ...initialState,
    product_interest: presetProduct || "",
  }));
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (presetProduct) setForm((f) => ({ ...f, product_interest: presetProduct }));
  }, [presetProduct]);

  useEffect(() => {
    const handler = (e) => {
      const slug = e.detail?.slug;
      if (slug) setForm((f) => ({ ...f, product_interest: slug }));
    };
    window.addEventListener("preselect-product", handler);
    return () => window.removeEventListener("preselect-product", handler);
  }, []);

  const update = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill name, email and message.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      toast.success("Thank you! Your inquiry has been sent.", {
        description: "Our team will get back to you within 24 hours.",
      });
      setForm(initialState);
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.response?.data?.message ||
        "Could not send inquiry. Please try again or call us directly.";
      const msg =
        typeof detail === "string"
          ? detail
          : "Please check your details and try again.";
      toast.error("Submission failed", { description: msg });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="inquiry"
      className="relative py-24 md:py-32 bg-[#FAF9F6] overflow-hidden"
      data-testid="inquiry-section"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9 }}
          className="flex items-center gap-4 mb-6"
        >
          <span className="h-px w-16 bg-[#D4AF37]" />
          <span className="font-accent text-[10px] text-[#475569]">
            Start the Conversation
          </span>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <motion.h2
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 1, ease: [0.19, 1, 0.22, 1] }}
              className="font-display font-bold text-[#0A192F] leading-[0.95] tracking-tight"
              data-testid="inquiry-heading"
              style={{ fontSize: "clamp(2.5rem, 5.2vw, 4.75rem)" }}
            >
              Let&apos;s build
              <br />
              <span className="italic font-normal text-[#D4AF37]">something</span>{" "}
              together.
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, delay: 0.15 }}
              className="mt-6 text-[#475569] text-base md:text-lg leading-relaxed max-w-md"
            >
              Share your product requirement, target quantity and destination
              country. We typically respond with a detailed quote within 24
              business hours.
            </motion.p>

            <div id="contact" className="mt-12 space-y-6" data-testid="contact-block">
              <ContactItem
                icon={Phone}
                label="Phone"
                lines={["+91 7276863734", "+91 8446226916"]}
                testid="contact-phone"
                href="tel:+917276863734"
              />
              <ContactItem
                icon={Mail}
                label="Email"
                lines={["rltrading2011@gmail.com"]}
                testid="contact-email"
                href="mailto:rltrading2011@gmail.com"
              />
              <ContactItem
                icon={MapPin}
                label="Address"
                lines={["Chinchwad, Pune 411019", "Maharashtra, India"]}
                testid="contact-address"
              />
            </div>

            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, delay: 0.3 }}
              className="mt-12 border-t border-[#E2E8F0] pt-8"
            >
              <div className="font-accent text-[10px] text-[#475569]">
                Business Hours
              </div>
              <div className="mt-2 text-sm text-[#0A192F]">
                Monday – Saturday · 09:30 – 18:30 IST
              </div>
            </motion.div>
          </div>

          <motion.form
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 1, ease: [0.19, 1, 0.22, 1] }}
            onSubmit={onSubmit}
            className="lg:col-span-7 bg-white border border-[#E2E8F0] p-8 md:p-12"
            data-testid="inquiry-form"
            noValidate
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <Field label="Full Name *" testid="field-name">
                <Input
                  required
                  value={form.name}
                  onChange={(e) => update("name", e.target.value)}
                  placeholder="Your name"
                  className="rounded-none border-[#E2E8F0] focus-visible:ring-1 focus-visible:ring-[#D4AF37] focus-visible:border-[#D4AF37]"
                  data-testid="input-name"
                />
              </Field>
              <Field label="Email *" testid="field-email">
                <Input
                  type="email"
                  required
                  value={form.email}
                  onChange={(e) => update("email", e.target.value)}
                  placeholder="you@company.com"
                  className="rounded-none border-[#E2E8F0] focus-visible:ring-1 focus-visible:ring-[#D4AF37] focus-visible:border-[#D4AF37]"
                  data-testid="input-email"
                />
              </Field>
              <Field label="Phone" testid="field-phone">
                <Input
                  value={form.phone}
                  onChange={(e) => update("phone", e.target.value)}
                  placeholder="+1 555 0123"
                  className="rounded-none border-[#E2E8F0] focus-visible:ring-1 focus-visible:ring-[#D4AF37] focus-visible:border-[#D4AF37]"
                  data-testid="input-phone"
                />
              </Field>
              <Field label="Country" testid="field-country">
                <Input
                  value={form.country}
                  onChange={(e) => update("country", e.target.value)}
                  placeholder="Destination country"
                  className="rounded-none border-[#E2E8F0] focus-visible:ring-1 focus-visible:ring-[#D4AF37] focus-visible:border-[#D4AF37]"
                  data-testid="input-country"
                />
              </Field>
              <Field label="Company" testid="field-company">
                <Input
                  value={form.company}
                  onChange={(e) => update("company", e.target.value)}
                  placeholder="Company name"
                  className="rounded-none border-[#E2E8F0] focus-visible:ring-1 focus-visible:ring-[#D4AF37] focus-visible:border-[#D4AF37]"
                  data-testid="input-company"
                />
              </Field>
              <Field label="Product of Interest" testid="field-product">
                <Select
                  value={form.product_interest}
                  onValueChange={(v) => update("product_interest", v)}
                >
                  <SelectTrigger
                    className="rounded-none border-[#E2E8F0] focus:ring-1 focus:ring-[#D4AF37]"
                    data-testid="select-product"
                  >
                    <SelectValue placeholder="Select a product" />
                  </SelectTrigger>
                  <SelectContent className="rounded-none">
                    {PRODUCT_OPTIONS.map((opt) => (
                      <SelectItem
                        key={opt.value}
                        value={opt.value}
                        data-testid={`product-option-${opt.value}`}
                      >
                        {opt.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              <Field label="Estimated Quantity" testid="field-quantity" full>
                <Input
                  value={form.quantity}
                  onChange={(e) => update("quantity", e.target.value)}
                  placeholder="e.g., 20 MT / month"
                  className="rounded-none border-[#E2E8F0] focus-visible:ring-1 focus-visible:ring-[#D4AF37] focus-visible:border-[#D4AF37]"
                  data-testid="input-quantity"
                />
              </Field>

              <Field label="Message *" testid="field-message" full>
                <Textarea
                  required
                  rows={5}
                  value={form.message}
                  onChange={(e) => update("message", e.target.value)}
                  placeholder="Tell us about your requirement, specifications and delivery terms..."
                  className="rounded-none border-[#E2E8F0] focus-visible:ring-1 focus-visible:ring-[#D4AF37] focus-visible:border-[#D4AF37]"
                  data-testid="input-message"
                />
              </Field>
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="group relative mt-8 inline-flex items-center gap-3 px-8 py-4 bg-[#0A192F] text-white text-[11px] font-semibold tracking-[0.18em] uppercase overflow-hidden disabled:opacity-60 disabled:cursor-not-allowed"
              data-testid="inquiry-submit-button"
            >
              <span className="absolute inset-0 bg-[#D4AF37] translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.22,1,0.36,1)]" />
              <span className="relative z-10 group-hover:text-[#0A192F] transition-colors duration-500 flex items-center gap-3">
                {submitting ? (
                  <>
                    <Loader2 size={16} className="animate-spin" /> Sending...
                  </>
                ) : (
                  <>
                    Send Inquiry <Send size={14} />
                  </>
                )}
              </span>
            </button>

            <p className="mt-4 text-xs text-[#475569]">
              By submitting, you agree to be contacted by Rahilaxmi Exports
              LLP regarding your inquiry.
            </p>
          </motion.form>
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, children, full = false, testid }) => (
  <div className={full ? "sm:col-span-2" : ""} data-testid={testid}>
    <Label className="font-accent text-[10px] text-[#475569] mb-2 inline-block">
      {label}
    </Label>
    {children}
  </div>
);

const ContactItem = ({ icon: Icon, label, lines, testid, href }) => {
  const content = (
    <div className="flex items-start gap-4">
      <div className="w-11 h-11 border border-[#0A192F]/15 bg-white flex items-center justify-center flex-shrink-0">
        <Icon size={18} className="text-[#0A192F]" strokeWidth={1.5} />
      </div>
      <div>
        <div className="font-accent text-[10px] text-[#475569]">{label}</div>
        <div className="mt-1 space-y-0.5">
          {lines.map((l) => (
            <div key={l} className="text-sm text-[#0A192F] font-medium">
              {l}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
  return href ? (
    <a
      href={href}
      data-testid={testid}
      className="block hover:opacity-80 transition-opacity"
    >
      {content}
    </a>
  ) : (
    <div data-testid={testid}>{content}</div>
  );
};

export default InquiryForm;
